/**
 * YUMIX — Global ErrorBoundary (Lot #11 hotfix).
 *
 * Catches ANY uncaught exception in the React render tree and renders a
 * minimal, branded fallback instead of the dreaded White/Black Screen of
 * Death. Two flavours:
 *   • <ErrorBoundary>          — full-screen fallback for top-level crashes
 *   • <ErrorBoundary silent>   — renders nothing on crash (use around
 *                                 cosmetic widgets like the LocalityWelcome
 *                                 banner so a buggy delight feature can't
 *                                 take the whole app down)
 *
 * Reports to /api/telemetry/error (fire-and-forget) when REACT_APP_BACKEND_URL
 * is available. Never blocks rendering.
 */
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    const payload = {
      message: String(error?.message || error || 'unknown').slice(0, 500),
      stack: String(error?.stack || '').slice(0, 2000),
      component_stack: String(errorInfo?.componentStack || '').slice(0, 2000),
      boundary: this.props.label || 'global',
      url: typeof window !== 'undefined' ? window.location?.href || '' : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
      timestamp: new Date().toISOString(),
    };

    // 1) Persist locally so the user can retrieve the stack via DevTools/Settings
    //    even when the network telemetry call is broken.
    try {
      const stash = JSON.parse(localStorage.getItem('yumix_last_crashes') || '[]');
      stash.unshift(payload);
      localStorage.setItem('yumix_last_crashes', JSON.stringify(stash.slice(0, 10)));
    } catch (_) { /* quota — ignore */ }

    // 2) Fire-and-forget telemetry — try BOTH the env-configured backend URL
    //    AND the same-origin /api/... path. One of them should land even if
    //    REACT_APP_BACKEND_URL is misconfigured in production.
    try {
      const envUrl =
        (typeof process !== 'undefined' &&
          process.env &&
          process.env.REACT_APP_BACKEND_URL) || '';
      const sameOrigin =
        (typeof window !== 'undefined' && window.location?.origin) || '';
      const candidates = [
        envUrl ? `${envUrl}/api/telemetry/frontend-error` : '',
        sameOrigin ? `${sameOrigin}/api/telemetry/frontend-error` : '',
      ].filter(Boolean);
      const seen = new Set();
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      candidates.forEach((url) => {
        if (seen.has(url)) return;
        seen.add(url);
        try {
          if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            navigator.sendBeacon(url, blob);
          } else if (typeof fetch !== 'undefined') {
            fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
              keepalive: true,
              credentials: 'omit',
            }).catch(() => {});
          }
        } catch (_) { /* swallow per-candidate */ }
      });
    } catch (_) {
      /* swallow */
    }

    // Always log to console so dev sees it in browser inspector.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label || 'global', error, errorInfo);
    // eslint-disable-next-line no-console
    console.info(
      '%c[YUMIX Crash Inspector]%c Copy the next line to share with support:\n' +
        '%cJSON.parse(localStorage.getItem("yumix_last_crashes"))',
      'color:#ff6b35;font-weight:bold',
      '',
      'color:#10b981;font-family:monospace'
    );
  }

  handleReload = () => {
    try {
      // Clear potentially-poisoned session keys but keep auth token.
      ['i18nextLng_pending'].forEach((k) => sessionStorage.removeItem(k));
    } catch (_) {}
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Silent mode: render nothing (e.g., around the LocalityWelcomeBanner).
    if (this.props.silent) return null;

    // Render the user-provided fallback if any.
    if (this.props.fallback) return this.props.fallback;

    // Default branded fallback.
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: '100vh',
          background: '#0a0a0a',
          color: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div style={{ maxWidth: '420px', textAlign: 'center' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
            }}
          >
            ⚠️
          </div>
          <h1
            style={{
              fontSize: '20px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '12px',
            }}
          >
            Oups, YUMIX a tropéssayé.
          </h1>
          <p style={{ fontSize: '14px', color: '#aaa', marginBottom: '24px', lineHeight: 1.5 }}>
            Un imprévu côté technique. Recharge la page — on est déjà au courant et on regarde
            ce qui s'est passé.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="error-boundary-reload-btn"
            style={{
              padding: '12px 24px',
              borderRadius: '24px',
              border: 'none',
              background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
            }}
          >
            Recharger
          </button>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: '24px', textAlign: 'left', fontSize: '11px' }}>
              <summary style={{ cursor: 'pointer', color: '#888' }}>Détails dev</summary>
              <pre
                style={{
                  marginTop: '8px',
                  padding: '12px',
                  background: '#1a1a1a',
                  borderRadius: '8px',
                  overflow: 'auto',
                  color: '#e74c3c',
                }}
              >
                {String(this.state.error?.stack || this.state.error)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
